import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ENTITY_KINDS = new Set(["account", "customer", "order", "internet", "italiamobile", "energia", "energia-legacy", "sim", "esim", "promozioni", "spedizioni"]);
const EntityLinksContext = createContext(null);
const makeResolver = () => {
  const cache = new Map(), waiting = new Map(), controllers = new Set();
  let timer, disposed = false;
  const flush = async () => {
    timer = null;
    const batch = [...waiting.keys()].filter((key) => !cache.has(key)).slice(0, 200);
    if (!batch.length || disposed) return;
    batch.forEach((key) => cache.set(key, null));
    const controller = new AbortController(); controllers.add(controller);
    try {
      const { data } = await api.post("/entity-links", { entities: batch.map((key) => { const [kind, id] = key.split(":"); return { kind, id }; }) }, { signal: controller.signal });
      if (!disposed) batch.forEach((key) => { const href = data.links?.[key] || null; cache.set(key, href); waiting.get(key)?.forEach((notify) => notify(href)); });
    } catch { /* Unavailable references stay plain text. */ }
    finally { controllers.delete(controller); if (!disposed) timer = setTimeout(flush, 0); }
  };
  return {
    activate() { disposed = false; cache.clear(); },
    subscribe(key, notify) {
      if (cache.has(key)) notify(cache.get(key));
      if (!waiting.has(key)) waiting.set(key, new Set());
      waiting.get(key).add(notify);
      if (!timer) timer = setTimeout(flush, 0);
      return () => { const listeners = waiting.get(key); listeners?.delete(notify); if (!listeners?.size) waiting.delete(key); };
    },
    dispose() { disposed = true; clearTimeout(timer); timer = null; controllers.forEach((controller) => controller.abort()); waiting.clear(); },
  };
};

export const EntityLinksProvider = ({ children }) => {
  const { user } = useAuth();
  const { key } = useLocation();
  const resolver = useMemo(makeResolver, [user?.id, user?.role, key]);
  useEffect(() => { resolver.activate(); return () => resolver.dispose(); }, [resolver]);
  return <EntityLinksContext.Provider value={resolver}>{children}</EntityLinksContext.Provider>;
};

export const EntityLink = ({ kind = "account", id, children, className = "", onClick, ...props }) => {
  const resolver = useContext(EntityLinksContext);
  const key = ENTITY_KINDS.has(kind) && typeof id === "string" && /^[a-f\d]{24}$/i.test(id) ? `${kind}:${id.toLowerCase()}` : null;
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    if (!key || !resolver) return;
    return resolver.subscribe(key, (href) => setResolved({ resolver, key, href }));
  }, [resolver, key]);
  const href = resolved?.resolver === resolver && resolved?.key === key ? resolved.href : null;
  if (!href) return <span className={className} {...props}>{children || "—"}</span>;
  return <Link to={href} className={`text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 rounded-sm transition-colors ${className}`} onClick={(event) => { event.stopPropagation(); onClick?.(event); }} {...props}>{children || "Apri scheda"}</Link>;
};
